import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './lib/api';
import { useIdleLogout, useTerminal } from './lib/store';
import { Empty } from './components/ui';
import { Login } from './screens/Login';
import { Home } from './screens/Home';
import { StartPhase } from './screens/StartPhase';
import { FinishPhase } from './screens/FinishPhase';
import { CloseOrder } from './screens/CloseOrder';
import { Monitor } from './screens/Monitor';
import { MonitorOF } from './screens/MonitorOF';
import { Admin } from './screens/Admin';
import { AdminConsole } from './screens/AdminConsole';
import { Direccion } from './screens/Direccion';

type Context =
  | { mode: 'admin' | 'tenant' | 'unknown'; tenantDomain: string; tenant: { slug: string; name: string; active: boolean; configured: boolean } | null }
  | null;

export function App() {
  const { state, catalog, toasts, dismiss, logout } = useTerminal();
  const location = useLocation();
  const [context, setContext] = useState<Context>(null);
  const [loading, setLoading] = useState(true);

  // Lo primero es saber qué dirección se ha abierto: el panel o el terminal de
  // un cliente. Es el servidor quien lo dice, a partir del subdominio.
  useEffect(() => {
    api
      .context()
      .then(setContext)
      .catch(() => setContext(null))
      .finally(() => setLoading(false));
  }, []);

  const idleSeconds = Number(catalog?.parameters['terminal.idleTimeoutSec'] ?? 120);
  const idleEnabled = state !== null && context?.mode === 'tenant' && !location.pathname.startsWith('/monitor');
  const onIdle = useCallback(() => logout(), [logout]);
  useIdleLogout(idleEnabled ? idleSeconds : 0, onIdle);

  if (loading) {
    return <div className="app"><div className="content"><Empty icon="⏳" title="Cargando…" /></div></div>;
  }

  if (context?.mode === 'admin') {
    return <AdminConsole tenantDomain={context.tenantDomain} />;
  }

  if (context?.mode === 'unknown' || !context) {
    return (
      <div className="app">
        <div className="content">
          <Empty
            icon="🧭"
            title="Esta dirección no corresponde a ningún cliente"
            body={`El terminal de cada cliente se sirve en <cliente>.${context?.tenantDomain ?? 'terminal.ecinsapp.es'}.`}
          />
        </div>
      </div>
    );
  }

  if (context.tenant && !context.tenant.configured) {
    return (
      <div className="app">
        <div className="content">
          <Empty
            icon="🛠️"
            title={`${context.tenant.name} todavía no está configurado`}
            body="Falta darle de alta su sociedad de SAP en el panel de administración. Hasta entonces el terminal no puede identificar a nadie."
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {state ? (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/start" element={<StartPhase />} />
          <Route path="/finish" element={<FinishPhase />} />
          <Route path="/close" element={<CloseOrder />} />
          <Route path="/monitor" element={<Monitor />} />
          <Route path="/monitor/of" element={<MonitorOF />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/direccion" element={<Direccion tenantName={context.tenant?.name ?? ''} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* La dirección entra con usuario y contraseña; el taller, con PIN. */}
          <Route path="/direccion" element={<Direccion tenantName={context.tenant?.name ?? ''} />} />
          <Route path="/monitor" element={<Monitor />} />
          <Route path="/monitor/of" element={<MonitorOF />} />
          <Route path="*" element={<Login tenantName={context.tenant?.name ?? null} />} />
        </Routes>
      )}

      <div className="toast-host">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast${toast.kind === 'ok' ? ' toast--ok' : toast.kind === 'error' ? ' toast--error' : ''}`}
            onClick={() => dismiss(toast.id)}
          >
            <div className="toast__title">{toast.title}</div>
            {toast.body ? <div className="toast__body">{toast.body}</div> : null}
          </div>
        ))}
      </div>
    </>
  );
}
